"use client";

import Image from 'next/image';
import TrashGif from '@/app/trash.gif';
import { useState, useEffect, useCallback } from 'react';
import { dmsApi } from '@/lib/api';
import { Trash2, Loader2, RotateCcw, Folder, File, ArrowLeft, ChevronRight, FileText, FileImage, FileArchive } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface TrashFile {
  type: 'file';
  id: string;
  originalName: string;
  pathSnapshot?: string;
  path?: string;
  size?: number;
  mimeType?: string;
  deletedAt?: string;
}

interface TrashFolder {
  type: 'folder';
  id: string;
  name: string;
  path?: string;
  deletedAt?: string;
  folders: TrashFolder[];
  files: TrashFile[];
}

type TrashItem = TrashFolder | TrashFile;

function isTrashFolder(item: TrashItem): item is TrashFolder {
  return item.type === 'folder';
}

function findFolderById(items: TrashFolder[], id: string): TrashFolder | null {
  for (const item of items) {
    if (item.type === 'folder' && item.id === id) return item;
    const found = findFolderById(item.folders ?? [], id);
    if (found) return found;
  }
  return null;
}

function getFileIcon(mimeType?: string, size: number = 48) {
  if (mimeType?.includes('pdf')) return <FileText size={size} className="text-red-500" />;
  if (mimeType?.includes('image')) return <FileImage size={size} className="text-blue-500" />;
  if (mimeType?.includes('zip') || mimeType?.includes('archive')) return <FileArchive size={size} className="text-orange-500" />;
  return <File size={size} className="text-muted-foreground" />;
}

function formatDate(iso?: string) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
}

export function TrashPage() {
  const [rawItems, setRawItems] = useState<TrashItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [pathStack, setPathStack] = useState<{ id: string; name: string }[]>([]);

  const loadTrash = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await dmsApi.trash.list();
      const list = data?.items ?? [];
      setRawItems(Array.isArray(list) ? list : []);
    } catch (err: any) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: err?.error ?? err?.message ?? 'Failed to load trash.',
      });
      setRawItems([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTrash();
  }, [loadTrash]);

  const currentFolderId = pathStack.length > 0 ? pathStack[pathStack.length - 1].id : null;
  const currentFolder = currentFolderId
    ? findFolderById(rawItems.filter(isTrashFolder), currentFolderId)
    : null;
  
  const rootFolders = rawItems.filter(isTrashFolder);
  const rootFiles = rawItems.filter((item): item is TrashFile => item.type === 'file');

  const folders = currentFolderId && currentFolder
    ? (currentFolder.folders ?? [])
    : rootFolders;
  const files = currentFolderId && currentFolder
    ? (currentFolder.files ?? [])
    : rootFiles;

  const enterFolder = (folder: TrashFolder) => {
    setPathStack((prev) => [...prev, { id: folder.id, name: folder.name }]);
  };

  const goBack = () => {
    setPathStack((prev) => prev.slice(0, -1));
  };

  const goToRoot = () => {
    setPathStack([]);
  };

  const handleRestore = async (id: string, type: 'folder' | 'file', e?: React.MouseEvent) => {
    e?.stopPropagation();
    setRestoringId(id);
    try {
      await dmsApi.trash.restore(id, type);
      toast({ title: 'Restored', description: 'Item has been restored.' });
      loadTrash();
      setPathStack((prev) => prev.filter((p) => p.id !== id));
    } catch (err: any) {
      toast({
        variant: 'destructive',
        title: 'Restore failed',
        description: err?.error ?? err?.message ?? 'Could not restore item.',
      });
    } finally {
      setRestoringId(null);
    }
  };

  return (
    <div className="flex flex-col h-full overflow-hidden bg-background">
      <Card className="m-6 flex-1 min-h-0 flex flex-col overflow-hidden">
        <CardHeader className="shrink-0">
          <CardTitle className="flex items-center gap-2">
            <Trash2 size={24} />
            Trash
          </CardTitle>
          <CardDescription>
            Deleted folders and files. Open a folder to see its contents; use Restore on a folder or file to restore it.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex-1 min-h-0 flex flex-col p-0">
          <div className="flex items-center gap-2 px-6 pb-3 shrink-0 border-b">
            <Button variant="ghost" size="icon" onClick={goBack} disabled={pathStack.length === 0}>
              <ArrowLeft size={18} />
            </Button>
            <div className="flex items-center gap-1 text-sm font-medium flex-wrap">
              <span
                className={cn(
                  pathStack.length === 0 ? 'font-bold text-primary' : 'text-muted-foreground hover:text-primary cursor-pointer'
                )}
                onClick={goToRoot}
              >
                Trash
              </span>
              {pathStack.map((p, idx) => (
                <div key={p.id} className="flex items-center gap-1">
                  <ChevronRight size={14} className="text-muted-foreground flex-shrink-0" />
                  <span
                    className={cn(
                      'truncate max-w-[140px]',
                      idx === pathStack.length - 1 ? 'font-bold text-primary cursor-default' : 'text-muted-foreground cursor-pointer hover:text-primary'
                    )}
                    onClick={() => setPathStack((prev) => prev.slice(0, idx + 1))}
                    title={p.name}
                  >
                    {p.name}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex-1 overflow-auto p-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : folders.length === 0 && files.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 gap-6 text-center">
                <div className="relative w-40 h-40 sm:w-52 sm:h-52">
                  <Image
                    src={TrashGif}
                    alt="Empty trash"
                    fill
                    className="object-contain drop-shadow-md"
                    priority
                  />
                </div>
                <div className="space-y-1">
                  <p className="text-base font-medium">Trash is empty</p>
                  <p className="text-sm text-muted-foreground max-w-md">
                    Deleted folders and files will appear here so you can restore them or remove them permanently.
                  </p>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-6">
                {folders.map((folder) => (
                  <div
                    key={folder.id}
                    className="group relative flex flex-col items-center gap-2 p-4 rounded-xl border border-transparent hover:bg-white hover:shadow-sm hover:border-border cursor-pointer transition-all"
                    onClick={() => enterFolder(folder)}
                  >
                    <div className="w-full aspect-square bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center group-hover:scale-105 transition-transform overflow-hidden">
                      <Folder size={48} fill="currentColor" className="opacity-80" />
                    </div>
                    <div className="text-center w-full">
                      <div className="text-sm font-medium truncate px-1" title={folder.name}>{folder.name}</div>
                      <div className="text-[10px] text-muted-foreground uppercase tracking-tight">Folder</div>
                      {folder.deletedAt && (
                        <div className="text-[10px] text-muted-foreground mt-0.5">{formatDate(folder.deletedAt)}</div>
                      )}
                    </div>
                    {pathStack.length === 0 && (
                      <Button
                        variant="secondary"
                        size="icon"
                        className="absolute top-2 right-2 h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                        onClick={(e) => handleRestore(folder.id, 'folder', e)}
                        disabled={restoringId !== null}
                        title="Restore folder"
                      >
                        {restoringId === folder.id ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : (
                          <RotateCcw size={16} />
                        )}
                      </Button>
                    )}
                  </div>
                ))}

                {files.map((file) => (
                  <div
                    key={file.id}
                    className="group relative flex flex-col items-center gap-2 p-4 rounded-xl border border-transparent hover:bg-white hover:shadow-sm hover:border-border transition-all"
                  >
                    <div className="w-full aspect-square bg-accent/5 text-accent rounded-2xl flex items-center justify-center group-hover:scale-105 transition-transform overflow-hidden">
                      {getFileIcon(file.mimeType, 48)}
                    </div>
                    <div className="text-center w-full">
                      <div className="text-sm font-medium truncate px-1" title={file.originalName}>{file.originalName}</div>
                      <div className="text-[10px] text-muted-foreground uppercase tracking-tight">File</div>
                      {file.deletedAt && (
                        <div className="text-[10px] text-muted-foreground mt-0.5">{formatDate(file.deletedAt)}</div>
                      )}
                    </div>
                    {pathStack.length === 0 && (
                      <Button
                        variant="secondary"
                        size="icon"
                        className="absolute top-2 right-2 h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                        onClick={(e) => handleRestore(file.id, 'file', e)}
                        disabled={restoringId !== null}
                        title="Restore file"
                      >
                        {restoringId === file.id ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : (
                          <RotateCcw size={16} />
                        )}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
